import clsx from 'clsx'
import { forwardRef, useMemo } from 'react'
import { CommonSelectProps, SelectBase } from '../SelectBase'
import { MultipleValue } from '../types'
import { useFloatingDropdown } from '../useFloatingDropdown'
import { Chip } from './Chip'
import styles from './SelectMultiple.module.scss'
import { useSelectMultiple } from './useSelectMultiple'
import { useMaxItemsDisplayed } from '@/hooks/useMaxItemsDisplayed'

export interface SelectMultipleProps extends CommonSelectProps {
  defaultValue?: MultipleValue
  value?: MultipleValue
  onChange?: (value: MultipleValue) => void
  /**
   * If provided amount of chips will be limited to this number. If more chips are selected, a `+n` chip will be shown. Automatically changes amount on resize.
   * @important Use this prop carefully. It can introduce performance issues if there are a lot of chips.
   */
  maxItemsDisplayed?: number
}

export const SelectMultiple = forwardRef<HTMLDivElement, SelectMultipleProps>(
  (
    {
      placeholder,
      children,
      defaultValue,
      defaultIsOpen = false,
      value,
      onChange,
      onBlur,
      onFocus,
      disabled = false,
      size = 'md',
      maxItemsDisplayed,
      dropdownMaxHeight,
      ...props
    },
    ref,
  ) => {
    const {
      refs,
      floatingStyles,
      context,
      isOpen,
      elementsRef,
      labelsRef,
      getReferenceProps,
      getFloatingProps,
      getItemProps,
      activeIndex,
    } = useFloatingDropdown({
      dropdownMaxHeight,
      defaultIsOpen,
      disabled,
    })

    const { selectedIndexes, handleSelect, selectedChildren } = useSelectMultiple({
      defaultValue,
      value,
      onChange,
      children,
    })

    const { hiddenCount, containerRef } = useMaxItemsDisplayed(selectedIndexes, maxItemsDisplayed)

    const selectContext = useMemo(
      () => ({
        activeIndex,
        selectedIndexes,
        getItemProps,
        handleSelect,
        size,
      }),
      [activeIndex, selectedIndexes, getItemProps, handleSelect, size],
    )

    return (
      <SelectBase
        ref={ref}
        disabled={disabled}
        size={size}
        isOpen={isOpen}
        refs={refs}
        floatingStyles={floatingStyles}
        context={context}
        children={children}
        canGrowVertically
        selectedChildren={
          <>
            {selectedChildren?.length === 0 ? (
              <div className={clsx('mui-select-multi-placeholder', styles.placeholder)}>
                {placeholder}
              </div>
            ) : null}
            <div
              ref={containerRef}
              className={clsx('mui-select-multi-chips-wrapper', styles.container, styles[size], {
                [styles.maxItemsDisplayed]: maxItemsDisplayed !== undefined,
              })}
            >
              {selectedChildren.map((child, index) => (
                <Chip
                  size={size}
                  key={index}
                  aria-hidden={index >= selectedChildren.length - hiddenCount}
                  className={clsx({
                    [styles.hiddenChip]: index >= selectedChildren.length - hiddenCount,
                  })}
                  icon={child.icon}
                  onRemove={() => handleSelect(child.index)}
                >
                  {child?.children}
                </Chip>
              ))}
              <Chip
                size={size}
                key="hidden-count"
                className={clsx({
                  [styles.hiddenChip]: hiddenCount <= 0,
                })}
              >
                +{hiddenCount}
              </Chip>
            </div>
          </>
        }
        referenceProps={getReferenceProps({ onBlur, onFocus })}
        floatingProps={getFloatingProps()}
        elementsRef={elementsRef}
        labelsRef={labelsRef}
        selectContext={selectContext}
        {...props}
      />
    )
  },
)

SelectMultiple.displayName = 'SelectMultiple'
